/**
 * Кабинеты заказчиков.
 *
 * Разовая ссылка на ТЗ никуда не делась — она удобна, когда надо отдать
 * один заказ и забыть. Кабинет для другого: постоянный клиент, которому
 * работа идёт потоком, и десяток ссылок в переписке он теряет.
 *
 * Колонка кабинета — это одно ТЗ, оно же один канал заказчика.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Empty, Loading, toast } from '../components/ui';
import { dateTime } from '../lib/format';
import type { Task } from '../lib/types';

type Column = {
  id: string;
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  label: string | null;
  total: number;
  done: number;
  marked: number;
  percent: number;
};

type Portal = {
  id: string;
  token: string;
  title: string;
  createdAt: string;
  lastAccessAt: string | null;
  columns: Column[];
};

export function ClientsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');

  const { data: portals, isLoading } = useQuery({
    queryKey: ['portals'],
    queryFn: () => api.get<Portal[]>('/api/portals'),
  });

  const { data: tasks } = useQuery({
    queryKey: ['tasks-all'],
    queryFn: () => api.get<Task[]>('/api/tasks?limit=200'),
  });

  const reload = () => void qc.invalidateQueries({ queryKey: ['portals'] });

  const create = useMutation({
    mutationFn: () => api.post('/api/portals', { title: title.trim(), password }),
    onSuccess: () => {
      reload();
      setCreating(false);
      setTitle('');
      setPassword('');
      toast('Кабинет создан');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const addColumn = useMutation({
    mutationFn: (v: { portalId: string; taskId: string }) =>
      api.post(`/api/portals/${v.portalId}/columns`, { taskId: v.taskId }),
    onSuccess: () => {
      reload();
      toast('ТЗ добавлено в кабинет');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const removeColumn = useMutation({
    mutationFn: (columnId: string) => api.del(`/api/portals/columns/${columnId}`),
    onSuccess: () => {
      reload();
      toast('Убрано из кабинета');
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/portals/${id}`),
    onSuccess: () => {
      reload();
      toast('Кабинет закрыт — ссылка больше не работает');
    },
  });

  const urlOf = (token: string) => `${window.location.origin}/p/${token}`;

  return (
    <>
      <PageHeader
        title="Кабинеты заказчиков"
        actions={
          !creating && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              + Новый кабинет
            </button>
          )
        }
      />
      <Page>
        {creating && (
          <div className="card mb-4 max-w-md">
            <div className="font-bold mb-1">Новый кабинет</div>
            <div className="text-[12.5px] text-muted2 leading-relaxed mb-3">
              Постоянный адрес для одного заказчика. Дальше добавляешь в него ТЗ —
              каждое станет отдельной колонкой, и заказчик подпишет их своими
              названиями каналов.
            </div>
            <div className="label">Название — видно заказчику</div>
            <input
              className="input mb-2"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Матвей"
              autoFocus
            />
            <div className="label">Пароль</div>
            <input
              className="input mb-3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="минимум 4 символа"
            />
            <div className="text-[11.5px] text-muted2 leading-relaxed mb-3">
              Пароль обязателен: кабинет живёт долго и копит всю работу, одного
              неугадываемого адреса тут мало. Передай его заказчику отдельно
              от ссылки.
            </div>
            <div className="flex gap-2">
              <button className="btn flex-1" onClick={() => setCreating(false)}>
                Отмена
              </button>
              <button
                className="btn btn-primary flex-1"
                disabled={!title.trim() || password.length < 4 || create.isPending}
                onClick={() => create.mutate()}
              >
                Создать
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <Loading />
        ) : !portals?.length ? (
          <Empty
            title="Кабинетов пока нет"
            hint="Кабинет удобен постоянному заказчику: один адрес вместо десятка ссылок на отдельные ТЗ."
          />
        ) : (
          <div className="space-y-4">
            {portals.map((p) => (
              <div key={p.id} className="card">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[15px]">{p.title}</div>
                    <div className="mono text-[11.5px] text-accent2 break-all mt-1">
                      {urlOf(p.token)}
                    </div>
                    <div className="text-[11.5px] text-muted2 mt-1">
                      создан {dateTime(p.createdAt)}
                      {p.lastAccessAt
                        ? ` · заходил ${dateTime(p.lastAccessAt)}`
                        : ' · ещё не заходил'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        void navigator.clipboard.writeText(urlOf(p.token));
                        toast('Ссылка скопирована', '⧉');
                      }}
                    >
                      Копировать
                    </button>
                    <button
                      className="btn btn-sm text-dang"
                      onClick={() => revoke.mutate(p.id)}
                      title="Кабинет перестанет открываться"
                    >
                      Закрыть
                    </button>
                  </div>
                </div>

                <div className="sec">Колонки · {p.columns.length}</div>
                {p.columns.length === 0 ? (
                  <div className="text-[12.5px] text-muted2 mb-3">
                    Пусто. Добавь ТЗ — оно станет колонкой.
                  </div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {p.columns.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-3 bg-bg rounded-lg px-3 py-2.5 flex-wrap"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold truncate">
                            {c.label ?? c.taskTitle}
                            {c.label && (
                              <span className="text-muted2 font-normal">
                                {' '}
                                · так назвал заказчик
                              </span>
                            )}
                          </div>
                          <div className="text-[11.5px] text-muted2">
                            ТЗ #{c.taskNumber} · {c.taskTitle}
                          </div>
                        </div>
                        <span className="mono text-[12px] text-muted">
                          {c.done}/{c.total} · {c.percent}%
                        </span>
                        {c.marked > 0 && (
                          <span className="pill bg-ok/10 text-ok">выложено {c.marked}</span>
                        )}
                        <button
                          className="btn btn-sm text-dang"
                          onClick={() => removeColumn.mutate(c.id)}
                        >
                          Убрать
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <select
                  className="input"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addColumn.mutate({ portalId: p.id, taskId: e.target.value });
                    }
                  }}
                >
                  <option value="">+ Добавить ТЗ колонкой…</option>
                  {tasks
                    ?.filter((t) => !p.columns.some((c) => c.taskId === t.id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        ТЗ #{t.number} · {t.title}
                      </option>
                    ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </Page>
    </>
  );
}
